import type { ReactNode } from 'react';

import { cn } from '@/lib/shadcn/utils';
import {
  slackAppRedirectUrl,
  type SlackTarget,
} from '@/modules/integrations/slackAppRedirectUrl';
import {
  Button,
  type ButtonProps,
} from '@/modules/ui/components/button/Button';

interface OpenSlackButtonProps {
  target: SlackTarget;
  label?: ReactNode;
  /** Runs on click while Slack opens in a new tab. */
  onOpen?: () => void;
  /**
   * Drops the `href` so the link can't be focused or activated by mouse or
   * keyboard; `aria-disabled` alone would still let Enter open Slack.
   */
  disabled?: boolean;
  buttonProps?: ButtonProps;
}

export const OpenSlackButton = ({
  target,
  label = 'Open Slack',
  onOpen,
  disabled = false,
  buttonProps,
}: OpenSlackButtonProps) => (
  <Button
    variant="primary"
    {...buttonProps}
    className={cn(
      'aria-disabled:pointer-events-none aria-disabled:opacity-50',
      buttonProps?.className,
    )}
    asChild
  >
    <a
      href={disabled ? undefined : slackAppRedirectUrl(target)}
      role={disabled ? 'link' : undefined}
      aria-disabled={disabled || undefined}
      target="_blank"
      rel="noopener noreferrer"
      onClick={disabled ? undefined : onOpen}
    >
      {label}
    </a>
  </Button>
);
